import React from "react";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { useColors } from "@helpers/hooks/useThemeColors";

type PushableButtonProps = {
  disabled?: boolean;
  size?: "md" | "sm";
  variant?: "primary" | "secondary" | "borderless" | "dashed";
  children?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (e: any) => void;
  type?: "button" | "submit" | "reset";
  isLoading?: boolean;
  buttonFunction?: "button" | "submit" | "reset";
  title?: string;
};

const PrettyButton: React.FC<PushableButtonProps> = ({
  disabled = false,
  size = "md",
  variant = "primary",
  children,
  onClick,
  type = "button",
  isLoading = false,
  buttonFunction,
  title,
}) => {
  const colors = useColors();
  const buttonType = buttonFunction || type;
  const buttonText = children || title;

  const buttonStyles = {
    position: "relative" as const,
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: disabled || isLoading ? "not-allowed" : "pointer",
    outlineOffset: "0.025rem",
    transition: "filter 250ms",
    opacity: disabled || isLoading ? 0.55 : 1,
    "&:hover": {
      filter: "brightness(110%)",
    },
    "&:focus:not(:focus-visible)": {
      outline: "none",
    },
    "& .MuiStack-root div": {
      minWidth: "100%",
      padding: "0 auto",
    },
  };

  const shadowStyles = {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "2rem",
    background: "rgba(0,0,0,0.01)",
    willChange: "transform",
    transform: "translateY(0.125rem)",
    transition: "transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1)",
  };

  const getEdgeStyles = () => {
    if (variant === "primary") {
      return colors.content.hover || "#000BFF";
    } else if (variant === "secondary") {
      return colors.main.primary;
    }
    return "none";
  };

  const edgeStyles = {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    borderRadius: "2rem",
    background: getEdgeStyles(),
  };

  const getFrontStyles = () => {
    const baseStyles = {
      display: "block",
      position: "relative" as const,
      padding: size === "sm" ? "0.3rem 1.25rem" : "0.6rem 2rem",
      borderRadius: "2rem",
      fontFamily: "var(--font-content)",
      fontSize: size === "sm" ? "1rem" : "1.25rem",
      fontWeight: size === "sm" ? 700 : 800,
      willChange: "transform",
      transform: "translateY(-0.25rem)",
      transition: "transform 600ms cubic-bezier(0.3, 0.7, 0.4, 1)",
      "& > span": {
        marginTop: "-0.125rem",
        marginBottom: "0.125rem",
        marginRight: "0.75rem",
      },
      "&:hover": {
        transform: disabled || isLoading ? "auto" : "translateY(-0.375rem)",
        transition:
          disabled || isLoading
            ? "auto"
            : "transform 250ms cubic-bezier(0.3, 0.7, 0.4, 1.5)",
      },
      "&:active": {
        transform: disabled || isLoading ? "auto" : "translateY(-0.125rem)",
        transition: disabled || isLoading ? "auto" : "transform 34ms",
      },
    };

    if (variant === "primary") {
      return {
        ...baseStyles,
        color: "white",
        border: "none",
        background: colors.main.primary,
      };
    } else if (variant === "secondary") {
      return {
        ...baseStyles,
        color: colors.main.primary,
        border: `1px solid ${colors.main.primary}`,
        background: "white",
      };
    } else if (variant === "borderless") {
      return {
        ...baseStyles,
        color: "#243050", // rgb(var(--color-secondary))
        border: "none",
        background: "#EFF6F9", // rgb(var(--color-bg-content))
      };
    } else if (variant === "dashed") {
      return {
        ...baseStyles,
        color: colors.main.primary,
        border: `3px dashed ${colors.main.primary}`,
        background: "white",
      };
    }
    return baseStyles;
  };

  const frontStyles = getFrontStyles();

  return (
    <Button
      type={buttonType}
      onClick={onClick}
      disabled={disabled || isLoading}
      sx={buttonStyles}
    >
      {variant === "primary" && <Box sx={shadowStyles} />}
      {(variant === "primary" || variant === "secondary") && (
        <Box sx={edgeStyles} />
      )}
      <Box sx={frontStyles}>
        <Stack direction="row" alignItems="center">
          {isLoading && (
            <CircularProgress
              size={20}
              sx={{ color: "white", marginRight: 2 }}
            />
          )}
          <div>{buttonText}</div>
        </Stack>
      </Box>
    </Button>
  );
};

export default PrettyButton;
